import { GridColDef } from '@mui/x-data-grid-pro';
import { ChangeEvent, useCallback, useState } from 'react';
import useSWR from 'swr';
import { dataGridCol, gridCurrencyCell } from '@shared/components/grid/common';
import { formatCurrency, gridDateTimeFormatter, mapGridIndexes } from '@shared/commonFunctions';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { SERVICES } from '@shared/constants/apiConfig';
import { ButtonWrapper } from '@styles/StyledDiv';
import { FileInput } from '@styles/StyledTextField';
import { Wrapper } from '@styles/CommonStyles';
import { LoadingButton } from '@shared/inputs/Buttons';
import { requestDeleteProductCancellation, requestImportFile } from './request';

export interface IProductCancellations {
  productCancellationCheckId: string;
  contractNumber: string;
  product: string;
  contractHolder: string;
  vinLast8: string;
  saleDate: string;
  cancelDate: string;
  cancelFactor: string;
  cancelReason: string;
  originalCustomerCost: number;
  originalSellerCost: number;
  cancelFee: number;
  customerRefund: number;
  refundFromSeller: number;
  paymentAmount: number;
  contactId: string;
  dealId: string;
  referenceNumber: string;
  lenderId: string;
  lenderName: string;
  assetTag?: string;
  customerName?: string;
  relatedDeal?: string;
  loanConsultant?: string;
  payeeName?: string;
  comment?: string;
  lenderCode? : string;
}

export interface IDealForCheckRequest extends  IProductCancellations {
  address: string;
  amountRequested: number;
}

const columns: GridColDef[] = [
  dataGridCol('contractNumber', {
    flex: 2,
    minWidth: 120,
  }),
  dataGridCol('product', {
    flex: 1,
    minWidth: 70,
  }),
  dataGridCol('contractHolder', {
    flex: 2,
    minWidth: 120,
  }),
  dataGridCol('vinLast8', {
    flex: 1,
    minWidth: 100,
    valueFormatter: ({ value }) => (value?.toUpperCase()),
  }),
  dataGridCol('saleDate', {
    flex: 1,
    minWidth: 150,
    type: 'dateTime',
    valueFormatter: gridDateTimeFormatter,
  }),
  dataGridCol('cancelDate', {
    flex: 1,
    minWidth: 150,
    type: 'dateTime',
    valueFormatter: gridDateTimeFormatter,
  }),
  dataGridCol('cancelFactor', {
    flex: 1,
    minWidth: 100,
  }),
  dataGridCol('cancelReason', {
    flex: 2,
    minWidth: 100,
  }),
  dataGridCol('originalCustomerCost', {
    flex: 1,
    minWidth: 100,
    ...gridCurrencyCell,
  }),
  dataGridCol('originalSellerCost', {
    flex: 1,
    minWidth: 100,
    valueFormatter: ({ value }) => formatCurrency(value),
  }),
  dataGridCol('customerRefund', {
    flex: 1,
    valueFormatter: ({ value }) => formatCurrency(value),
  }),
  dataGridCol('refundFromSeller', {
    flex: 1,
    valueFormatter: ({ value }) => formatCurrency(value),
  }),
  dataGridCol('paymentAmount', {
    flex: 1,
    minWidth: 100,
    valueFormatter: ({ value }) => formatCurrency(value),
  }),
].map((i) => ({ miwidth: 100, ...i }));

const gridActions = {
  onDelete: (row: IProductCancellations) => requestDeleteProductCancellation(row.productCancellationCheckId),
};

const ACCEPTED_FILES = '.csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, .pdf, application/pdf';

export const ImportProductCancel = () => {
  const { data = [], mutate, error } = useSWR<IProductCancellations[]>(
    `${SERVICES.ACCOUNTING}/productcancellations/checks?displayChecksValue=0
  `
  );
  const [loading, setLoading] = useState(false);

  const handleFiles = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    setLoading(true);
    const fileList = event.target.files || {};
    const files: File[] = Object.values(fileList);
    await requestImportFile(files);
    await mutate();
    setLoading(false);
  }, []);

  return (
    <Wrapper>
      <ButtonWrapper>
        <LoadingButton
          loading={loading}
          loadingIndicator="Uploading..."
          variant="contained"
          component="label"
          sx={{ position: 'absolute', right: '25px', marginTop: '0.6rem' }}
        >
          Select Files...
          <FileInput
            type={'file'}
            inputProps={{ accept: ACCEPTED_FILES }}
            onChange={handleFiles}
          />
        </LoadingButton>
      </ButtonWrapper>
      <PhoenixGrid
        autoHeight
        withHeader
        noButton
        strict
        canNotEdit
        canNotCopy
        showClearAllFilter
        name="Import Product Cancellations Queue"
		loading={!error && !data}
        columns={columns}
        rows={mapGridIndexes(data) || []}
        actions={gridActions}
      />
    </Wrapper>
  );
};
